import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { notificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';

export const NotificationContext = createContext();

// Хук для использования контекста уведомлений
export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const auth = useAuth();
  // Хранение ID полученных уведомлений для предотвращения дубликатов
  const receivedNotifications = useRef(new Set());

  // Инициализация WebSocket соединения только один раз
  useEffect(() => {
    // Очищаем предыдущие соединения при размонтировании
    return () => {
      if (socketRef.current) {
        console.log('🔌 Закрытие предыдущего соединения Socket.io...');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Подключение к WebSocket только при авторизации
  useEffect(() => {
    
    if (!auth.user || socketRef.current) return;

    // Определяем URL для подключения
    const socketUrl = process.env.REACT_APP_SOCKET_URL;
    console.log('🚀 Подключение к Socket.io:', socketUrl);
    console.log('👤 ID пользователя:', auth.user._id);
    console.log('🌍 Окружение:', process.env.NODE_ENV || 'development');

    try {
      // Создаем единственное соединение с улучшенными настройками для продакшена
      socketRef.current = io(socketUrl, {
        transports: ['websocket', 'polling'], // Сначала пробуем WebSocket, потом polling
        reconnection: true,
        reconnectionAttempts: 10, // Увеличиваем количество попыток
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000, // Увеличиваем таймаут
        forceNew: false, // Не создаем новое соединение
        auth: {
          token: localStorage.getItem('token')
        },
        // Дополнительные настройки для продакшена
        upgrade: true,
        rememberUpgrade: true,
        // Отключаем автоматическое переключение на polling при ошибках WebSocket
        autoConnect: true,
        // Добавляем обработку ошибок транспорта
        transportOptions: {
          polling: {
            extraHeaders: {
              'X-Client-Domain': window.location.origin
            }
          }
        }
      });

      console.log('📡 Инициализация Socket.io клиента...');

      socketRef.current.on('connect', () => {
        console.log('✅ Соединение установлено! ID:', socketRef.current.id);
        console.log('🔗 Транспорт:', socketRef.current.io.engine.transport.name);
        
        // Присоединение к комнате для получения уведомлений
        const userId = auth.user._id;
        console.log('🔗 Запрос на присоединение к комнате пользователя:', userId);
        socketRef.current.emit('join', { userId });
      });

      socketRef.current.on('joined', (data) => {
        console.log('✅ Присоединился к комнате:', data);
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('❌ Ошибка подключения:', error.message);
        console.error('🔍 Детали ошибки:', error);
        
        // Логируем текущий транспорт, но не пытаемся его изменить
        console.log('🔗 Текущий транспорт:', socketRef.current.io.engine.transport.name);
        console.log('⚠️ Socket.IO автоматически попробует другие транспорты');
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('❌ Соединение разорвано. Причина:', reason);
        
        // Если соединение разорвано сервером, пытаемся переподключиться
        if (reason === 'io server disconnect') {
          console.log('🔄 Сервер разорвал соединение, пытаемся переподключиться...');
          socketRef.current.connect();
        }
      });

      socketRef.current.on('error', (error) => {
        console.error('❌ Общая ошибка Socket.io:', error);
      });

      // Обработка изменения транспорта
      socketRef.current.io.on('upgrade', () => {
        console.log('🔄 Транспорт обновлен на:', socketRef.current.io.engine.transport.name);
      });

      socketRef.current.io.on('upgradeError', (error) => {
        console.error('❌ Ошибка обновления транспорта:', error);
        console.log('⚠️ Socket.IO автоматически попробует другие транспорты');
        console.log('🔗 Текущий транспорт:', socketRef.current.io.engine.transport.name);
      });

      // Обработка уведомлений с проверкой на дубликаты
      socketRef.current.on('notification', (notification) => {
        console.log('📩 Новое уведомление:', notification);
        
        // Проверяем, не получали ли мы уже это уведомление
        if (notification._id && !receivedNotifications.current.has(notification._id)) {
          // Добавляем ID в список полученных
          receivedNotifications.current.add(notification._id);
          
          // Добавляем уведомление в список и обновляем счетчик
          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);
        } else {
          console.log('🔄 Дубликат уведомления, игнорируем:', notification._id);
        }
      });

      // Обработчик выхода пользователя
      return () => {
        if (socketRef.current) {
          console.log('🔌 Отключение при смене пользователя...');
          socketRef.current.disconnect();
          socketRef.current = null;
          // Очищаем список полученных уведомлений
          receivedNotifications.current.clear();
        }
      };
    } catch (error) {
      console.error('❌ Ошибка инициализации Socket.io:', error);
      setError('Ошибка подключения к серверу уведомлений: ' + error.message);
    }
  }, [auth.user]);

  // Получение уведомлений при монтировании или смене пользователя
  useEffect(() => {
    if (auth.user) {
      console.log('📋 Получение уведомлений для пользователя:', auth.user._id);
      
      setLoading(true);
      notificationsAPI.getNotifications()
        .then(data => {
          console.log('📋 Получены уведомления:', data);
          setNotifications(data);
          
          // Добавляем все ID в список полученных уведомлений
          data.forEach(notification => {
            if (notification._id) {
              receivedNotifications.current.add(notification._id);
            }
          });
          
          // Подсчет непрочитанных уведомлений
          const unread = data.filter(notification => !notification.read).length;
          setUnreadCount(unread);
          setLoading(false);
        })
        .catch(err => {
          console.error('❌ Ошибка получения уведомлений:', err);
          setError(`Ошибка получения уведомлений: ${err.message}`);
          setLoading(false);
        });
    } else {
      // Если пользователь не авторизован, очищаем данные
      setNotifications([]);
      setUnreadCount(0);
      receivedNotifications.current.clear();
    }
  }, [auth.user]);

  // Отметить уведомление как прочитанное
  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      
      // Обновляем локальный массив уведомлений
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      // Уменьшаем счетчик непрочитанных
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      return true;
    } catch (error) {
      console.error('❌ Ошибка при отметке уведомления как прочитанное:', error);
      setError(`Ошибка при отметке уведомления: ${error.message}`);
      return false;
    }
  };

  // Отметить все уведомления как прочитанные
  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      
      // Обновляем локальный массив уведомлений
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      // Обнуляем счетчик непрочитанных
      setUnreadCount(0);
      
      return true;
    } catch (error) {
      console.error('❌ Ошибка при отметке всех уведомлений как прочитанные:', error);
      setError(`Ошибка при отметке всех уведомлений: ${error.message}`);
      return false;
    }
  };

  // Удалить уведомление
  const deleteNotification = async (notificationId) => {
    try {
      await notificationsAPI.deleteNotification(notificationId);
      
      // Проверяем, было ли уведомление непрочитанным
      const wasUnread = notifications.find(n => n._id === notificationId && !n.read);
      
      // Удаляем из локального массива
      setNotifications(prev => prev.filter(notification => notification._id !== notificationId));
      
      // Удаляем из списка полученных уведомлений
      if (notificationId) {
        receivedNotifications.current.delete(notificationId);
      }
      
      // Если удаленное уведомление было непрочитанным, уменьшаем счетчик
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      return true;
    } catch (error) {
      console.error('❌ Ошибка при удалении уведомления:', error);
      setError(`Ошибка при удалении уведомления: ${error.message}`);
      return false;
    }
  };

  // Удалить все уведомления
  const clearAllNotifications = async () => {
    try {
      await notificationsAPI.clearAllNotifications();
      
      // Очищаем локальный массив и счетчик
      setNotifications([]);
      setUnreadCount(0);
      receivedNotifications.current.clear();
      
      return true;
    } catch (error) {
      console.error('❌ Ошибка при удалении всех уведомлений:', error);
      setError(`Ошибка при удалении всех уведомлений: ${error.message}`);
      return false;
    }
  };
  
  // Обновить список уведомлений с сервера
  const refreshNotifications = async () => {
    try {
      console.log('🔄 Принудительное обновление уведомлений...');
      setLoading(true);
      
      const data = await notificationsAPI.getNotifications();
      console.log('📋 Получены обновленные уведомления:', data);
      
      // Обновляем список уведомлений
      setNotifications(data);
      
      // Обновляем список полученных ID
      receivedNotifications.current.clear();
      data.forEach(notification => {
        if (notification._id) {
          receivedNotifications.current.add(notification._id);
        }
      });
      
      // Пересчитываем непрочитанные
      const unread = data.filter(notification => !notification.read).length;
      setUnreadCount(unread);
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('❌ Ошибка обновления уведомлений:', error);
      setError(`Ошибка обновления уведомлений: ${error.message}`);
      setLoading(false);
      return false;
    }
  };

  return (
    <NotificationContext.Provider 
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications,
        refreshNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext; 